/**
 * Real-time voice session (Gemini Live via Firebase AI Logic).
 *
 * DESCOBERTA SOBRE O SDK (firebase@12.15.0, `@firebase/ai` dist/esm/index.esm.js):
 * `LiveSession.receive()` é documentado como "This can only be used by one
 * consumer at a time" e itera diretamente `this._serverMessages`, um ÚNICO
 * AsyncGenerator criado em `connect()` (`_serverMessages = _webSocketHandler.listen()`).
 * O helper `startAudioConversation` instancia um `AudioConversationRunner` cujo
 * `runReceiveLoop()` chama `this.liveSession.receive()` e o consome com
 * exclusividade — cada `next()` retira a mensagem do gerador subjacente, então
 * um segundo consumidor competiria pelas mensagens (cada mensagem iria para
 * apenas um deles, de forma não determinística). Além disso o runner IGNORA
 * `inputTranscription`/`outputTranscription`/`turnComplete`/`goingAway` e o seu
 * `functionCallingHandler` só permite devolver UMA FunctionResponse por
 * mensagem `toolCall` (`sendFunctionResponses([functionResponse])`).
 *
 * DECISÃO: implementamos o pipeline de áudio manualmente (sem
 * `startAudioConversation`), sendo o único consumidor de `receive()`:
 * - Captura: `AudioContext` na taxa nativa + `AudioWorkletNode` que reamostra
 *   para 16 kHz e converte Float32→Int16 LE em chunks de ~150ms, enviados via
 *   `session.sendAudioRealtime({ data: base64, mimeType: 'audio/pcm' })`.
 *   (Usamos a taxa nativa + reamostragem no worklet — o mesmo que o SDK faz —
 *   porque `AudioContext({sampleRate:16000})` falha ao conectar um
 *   MediaStreamSource com taxa diferente em alguns navegadores.)
 * - Playback: `AudioContext({ sampleRate: 24000 })` + fila de
 *   `AudioBufferSourceNode` agendados sem lacunas a partir de
 *   `modelTurn.parts[].inlineData` (base64 PCM 16-bit). Em `interrupted`
 *   (barge-in) a fila é limpa e as fontes agendadas são paradas.
 * - Tool calls: mensagens `toolCall` do `receive()`; TODAS as chamadas de um
 *   turno são executadas (callable `executeAgentTool`) e respondidas juntas em
 *   um único `sendFunctionResponses([...])`.
 */
import type { AgentToolName, MessagePart, ToolResult } from '@agente/shared'
import {
  GEMINI_MODELS,
  VOICE_RECONNECT_BASE_DELAY_MS,
  VOICE_RECONNECT_MAX_ATTEMPTS,
  VOICE_SESSION_HARD_CAP_SECONDS,
} from '@agente/shared'
import type {
  FunctionResponse,
  LiveServerContent,
  LiveServerToolCall,
  LiveSession,
  LiveSessionResumptionUpdate,
} from 'firebase/ai'
import { useCallback, useEffect, useRef, useState } from 'react'

import { agentToolLabel } from '@/components/chat/tool-chip'
import {
  decideLoopEnd,
  type LoopEndReason,
  nextResumptionHandle,
  reconnectDelayMs,
} from '@/hooks/voice-session-utils'
import { createLiveModel } from '@/lib/ai'
import { useAuth } from '@/lib/auth'
import { executeAgentTool, getVoiceSessionConfig, recordVoiceUsage } from '@/lib/callables'
import {
  createVoiceConversation,
  persistVoiceMarker,
  persistVoiceTurn,
} from '@/lib/voice-persistence'

/**
 * Teto local (segundos) quando a resumption NÃO pôde ser habilitada (fallback do
 * handshake para `connect()` sem argumento). Sem resumption a sessão não
 * sobrevive à queda de ~10 min da Live API, então encerramos aos 9 min como
 * antes. Com resumption ativa vale `VOICE_SESSION_HARD_CAP_SECONDS`.
 */
const FALLBACK_CAP_SECONDS = 540

export type VoiceSessionStatus =
  | 'idle'
  | 'requesting-mic'
  | 'connecting'
  | 'reconnecting'
  | 'listening'
  | 'speaking'
  | 'executing-tool'
  | 'awaiting-confirmation'
  | 'ending'
  | 'ended'
  | 'error'

/** Ação de escrita proposta pela voz, aguardando confirmação explícita. */
export interface VoicePendingConfirmation {
  /** Resumo pt-BR da ação exata que executará ao confirmar. */
  summary: string
}

export interface VoiceTranscript {
  /** Última fala (parcial ou completa) do usuário. */
  user: string
  /** Última fala (parcial ou completa) do agente. */
  assistant: string
}

export interface UseVoiceSessionOptions {
  /** Chamado quando a sessão cria uma conversa nova para persistir os turnos. */
  onConversationCreated?: (conversationId: string) => void
}

export interface UseVoiceSessionResult {
  status: VoiceSessionStatus
  /** Mensagem de erro pt-BR quando `status === 'error'`. */
  error: string | null
  /** Label pt-BR da ferramenta em execução (status `executing-tool`). */
  toolLabel: string | null
  /**
   * Segundos decorridos desde o início da sessão. Contínuo — atravessa as
   * reconexões (a resumption torna a queda de ~10 min transparente).
   */
  elapsed: number
  transcript: VoiceTranscript
  /**
   * `false` quando a sessão conectou em modo reduzido (fallback sem as
   * ferramentas do agente: agenda/lembretes indisponíveis nesta sessão).
   * Otimista (`true`) até o handshake concluir.
   */
  toolsEnabled: boolean
  /**
   * `false` quando a sessão conectou sem transcrição de entrada/saída (piso de
   * degradação): os turnos não são transcritos no chat e só um marcador de
   * sessão é persistido ao encerrar. Otimista (`true`) até o handshake concluir.
   */
  transcriptionEnabled: boolean
  /**
   * `true` quando o modelo preferido (`config.model`) falhou em todas as variantes
   * e a sessão subiu no modelo default (`GEMINI_MODELS.live`). Otimista (`false`)
   * até o handshake concluir.
   */
  modelDowngraded: boolean
  /**
   * Ação de escrita aguardando confirmação (a trava de escritas). Quando não é
   * null, o modelo está pausado esperando `confirmPendingAction`/`cancelPendingAction`.
   */
  pendingConfirmation: VoicePendingConfirmation | null
  /** Confirma a ação pendente: reexecuta a tool com `confirmed:true` e libera o modelo. */
  confirmPendingAction: () => void
  /** Cancela a ação pendente: devolve um cancelamento ao modelo e libera-o. */
  cancelPendingAction: () => void
  start: (conversationId?: string) => Promise<void>
  stop: () => Promise<void>
}

const INPUT_SAMPLE_RATE = 16_000
const OUTPUT_SAMPLE_RATE = 24_000
/** ~150ms de áudio a 16 kHz por chunk enviado. */
const CAPTURE_CHUNK_SAMPLES = 2_400
const CAPTURE_PROCESSOR_NAME = 'voice-capture-processor'

/**
 * Variantes de config para o handshake do Live, na ordem de preferência: o
 * primeiro `connect()` que funcionar vence. Três dimensões degradáveis, da mais
 * valiosa para o piso:
 * - transcrição: preferida LIGADA (persiste os turnos no chat). Native-audio no
 *   Vertex aceita transcrição desde que `responseModalities` seja só AUDIO — ver
 *   `createLiveModel` em `lib/ai`. Para cada combinação de ferramentas/voz
 *   tentamos primeiro COM transcrição e, se o handshake falhar, SEM ela; nesse
 *   piso a persistência degrada para o marcador de sessão (`persistVoiceMarker`).
 * - ferramentas do agente (agenda/lembretes/clientes): desligar é o último
 *   recuo, mantendo ao menos a voz no ar.
 * - voz escolhida: recua para a voz default do modelo (pode recusar uma prebuilt
 *   específica) antes de desligar as ferramentas.
 * - compressão da janela de contexto: preferida LIGADA (sessão longa dentro do
 *   orçamento de tokens); recua para DESLIGADA se o setup a rejeitar (mesmo
 *   padrão de fallback do native-audio já descrito). É a dimensão mais externa —
 *   só recuamos a compressão depois de esgotar todas as demais combinações.
 */
const LIVE_CONNECT_VARIANTS: readonly {
  tools: boolean
  voice: boolean
  transcription: boolean
  compress: boolean
}[] = [
  // Com compressão da janela de contexto primeiro.
  { tools: true, voice: true, transcription: true, compress: true }, // preferida: tudo ligado
  { tools: true, voice: true, transcription: false, compress: true }, // sem transcrição (só marcador)
  { tools: true, voice: false, transcription: true, compress: true }, // recua para a voz default
  { tools: true, voice: false, transcription: false, compress: true },
  { tools: false, voice: false, transcription: true, compress: true }, // desliga ferramentas
  { tools: false, voice: false, transcription: false, compress: true }, // piso com compressão
  // Sem compressão (o setup pode rejeitar o campo).
  { tools: true, voice: true, transcription: true, compress: false },
  { tools: true, voice: true, transcription: false, compress: false },
  { tools: true, voice: false, transcription: true, compress: false },
  { tools: true, voice: false, transcription: false, compress: false },
  { tools: false, voice: false, transcription: true, compress: false },
  { tools: false, voice: false, transcription: false, compress: false }, // piso: só prova a voz
]

/**
 * AudioWorkletProcessor de captura: reamostra a taxa nativa do contexto para
 * 16 kHz (interpolação simples), converte Float32 → Int16 e acumula ~150ms
 * antes de postar para a main thread (que faz base64 + envio ao Live API).
 */
const CAPTURE_WORKLET_SOURCE = `
class VoiceCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super()
    this.targetSampleRate = options.processorOptions.targetSampleRate
    this.chunkSamples = options.processorOptions.chunkSamples
    this.queue = []
    this.queued = 0
  }

  process(inputs) {
    const channel = inputs[0] && inputs[0][0]
    if (channel && channel.length > 0) {
      // 'sampleRate' é global no AudioWorkletGlobalScope (taxa nativa do contexto).
      const ratio = sampleRate / this.targetSampleRate
      const outLength = Math.max(1, Math.round(channel.length / ratio))
      const int16 = new Int16Array(outLength)
      for (let i = 0; i < outLength; i++) {
        const sample = Math.max(-1, Math.min(1, channel[Math.min(channel.length - 1, Math.floor(i * ratio))]))
        int16[i] = sample < 0 ? sample * 32768 : sample * 32767
      }
      this.queue.push(int16)
      this.queued += int16.length
      if (this.queued >= this.chunkSamples) {
        const merged = new Int16Array(this.queued)
        let offset = 0
        for (const part of this.queue) {
          merged.set(part, offset)
          offset += part.length
        }
        this.queue = []
        this.queued = 0
        this.port.postMessage(merged, [merged.buffer])
      }
    }
    return true
  }
}
registerProcessor('${CAPTURE_PROCESSOR_NAME}', VoiceCaptureProcessor)
`

function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength)
  let binary = ''
  const step = 0x8000
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step))
  }
  return btoa(binary)
}

function base64ToInt16(base64: string): Int16Array {
  const raw = atob(base64)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  // Um chunk truncado do servidor pode ter comprimento ímpar; `Int16Array` exige
  // um buffer múltiplo de 2 (senão lança RangeError e derruba o playback).
  // Descarta o último byte quando o comprimento for ímpar.
  const usableBytes = bytes.length - (bytes.length % 2)
  return new Int16Array(bytes.buffer, 0, usableBytes / 2)
}

function mapStartError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError') {
      return 'Não foi possível acessar o microfone. Verifique as permissões do navegador.'
    }
    if (err.name === 'NotFoundError') {
      return 'Nenhum microfone encontrado.'
    }
  }
  return 'Não foi possível iniciar a conversa por voz. Tente novamente em instantes.'
}

interface TurnBuffer {
  user: string
  assistant: string
  toolParts: MessagePart[]
}

function emptyTurn(): TurnBuffer {
  return { user: '', assistant: '', toolParts: [] }
}

function turnHasContent(turn: TurnBuffer): boolean {
  return (
    turn.user.trim().length > 0 || turn.assistant.trim().length > 0 || turn.toolParts.length > 0
  )
}

/** Recursos vivos de uma sessão; destruídos integralmente em `stop()`/unmount. */
interface SessionInternals {
  stopped: boolean
  session: LiveSession | null
  captureContext: AudioContext | null
  playbackContext: AudioContext | null
  mediaStream: MediaStream | null
  sourceNode: MediaStreamAudioSourceNode | null
  workletNode: AudioWorkletNode | null
  workletUrl: string | null
  /** Fontes de playback agendadas (canceladas em barge-in/stop). */
  scheduledSources: AudioBufferSourceNode[]
  nextStartTime: number
  timer: ReturnType<typeof setInterval> | null
  startedAt: number
  /**
   * Segundos de áudio de SAÍDA (modelo) efetivamente enfileirados para playback —
   * soma de `buffer.duration` de cada trecho PCM recebido em `enqueuePlayback`.
   * Medida exata (não é estimativa): reflete o áudio que o modelo de fato
   * produziu, reportado a `recordVoiceUsage` no `stop()` para custo por pessoa.
   */
  outputAudioSeconds: number
  conversationId: string | null
  turn: TurnBuffer
  /** Serializa as escritas no Firestore preservando a ordem dos turnos. */
  persistChain: Promise<void>
  persistedAnyTurn: boolean
  /** A sessão chegou a produzir áudio (usado no marcador de degradação). */
  receivedAudio: boolean
  /** Variante do handshake que venceu: `true` se as ferramentas do agente subiram. */
  toolsEnabled: boolean
  /**
   * Variante do handshake que venceu: `true` se a transcrição de entrada/saída
   * subiu. Quando `false`, a sessão não produz turnos transcritos e a
   * persistência degrada para o marcador em `stop()`.
   */
  transcriptionEnabled: boolean
  /** Handshake caiu no modelo default por falha do preferido em todas as variantes. */
  modelDowngraded: boolean
  /**
   * Último handle de resumption emitido pelo servidor (null até o primeiro update
   * retomável). É o que permite retomar a MESMA conversa após a queda do WS.
   */
  resumptionHandle: string | null
  /** Session resumption foi habilitada no handshake (`connect({})` venceu). */
  resumptionEnabled: boolean
  /** Uma reconexão está em curso — impede loops de receive() concorrentes. */
  reconnecting: boolean
  /** Tentativas de reconexão consumidas no ciclo atual (zeradas ao reconectar). */
  reconnectAttempts: number
  /**
   * Resolve a decisão de uma confirmação pendente (a trava de escritas). Definido
   * enquanto o `handleToolCall` aguarda o usuário; `stop()`/liberação o resolvem
   * como 'cancel' para nunca deixar a Promise pendurada.
   */
  pendingConfirmationResolve: ((decision: 'confirm' | 'cancel') => void) | null
}

export function useVoiceSession(options: UseVoiceSessionOptions = {}): UseVoiceSessionResult {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [status, setStatus] = useState<VoiceSessionStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [toolLabel, setToolLabel] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [transcript, setTranscript] = useState<VoiceTranscript>({ user: '', assistant: '' })
  // Otimista: assume ferramentas até o handshake resolver a variante real.
  const [toolsEnabled, setToolsEnabled] = useState(true)
  // Otimista: assume transcrição até o handshake resolver a variante real.
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(true)
  // Otimista: assume o modelo preferido até o handshake resolver o fallback.
  const [modelDowngraded, setModelDowngraded] = useState(false)
  const [pendingConfirmation, setPendingConfirmation] = useState<VoicePendingConfirmation | null>(
    null,
  )

  const internalsRef = useRef<SessionInternals | null>(null)
  const statusRef = useRef(status)
  statusRef.current = status
  const onConversationCreatedRef = useRef(options.onConversationCreated)
  onConversationCreatedRef.current = options.onConversationCreated
  const uidRef = useRef(uid)
  uidRef.current = uid

  /** Para o playback agendado e limpa a fila (barge-in ou encerramento). */
  const clearPlayback = useCallback((internals: SessionInternals) => {
    for (const source of [...internals.scheduledSources]) {
      try {
        source.stop(0)
      } catch {
        // já parada — ignore
      }
    }
    internals.scheduledSources = []
    if (internals.playbackContext) {
      internals.nextStartTime = internals.playbackContext.currentTime
    }
  }, [])

  /** Libera TODOS os recursos (mic, contexts, worklet, sessão, timer). */
  const releaseResources = useCallback(
    (internals: SessionInternals) => {
      internals.stopped = true
      // Nunca deixa uma confirmação pendente pendurada: resolve como cancelada
      // para o `handleToolCall` desbloquear e o loop encerrar limpo.
      if (internals.pendingConfirmationResolve) {
        const resolve = internals.pendingConfirmationResolve
        internals.pendingConfirmationResolve = null
        resolve('cancel')
      }
      if (internals.timer) {
        clearInterval(internals.timer)
        internals.timer = null
      }
      clearPlayback(internals)
      if (internals.workletNode) {
        internals.workletNode.port.onmessage = null
        internals.workletNode.disconnect()
        internals.workletNode = null
      }
      if (internals.sourceNode) {
        internals.sourceNode.disconnect()
        internals.sourceNode = null
      }
      // Libera as tracks do microfone — o indicador do navegador deve apagar.
      if (internals.mediaStream) {
        for (const track of internals.mediaStream.getTracks()) track.stop()
        internals.mediaStream = null
      }
      if (internals.captureContext && internals.captureContext.state !== 'closed') {
        void internals.captureContext.close().catch(() => undefined)
      }
      internals.captureContext = null
      if (internals.playbackContext && internals.playbackContext.state !== 'closed') {
        void internals.playbackContext.close().catch(() => undefined)
      }
      internals.playbackContext = null
      if (internals.workletUrl) {
        URL.revokeObjectURL(internals.workletUrl)
        internals.workletUrl = null
      }
      if (internals.session && !internals.session.isClosed) {
        void internals.session.close().catch(() => undefined)
      }
      internals.session = null
    },
    [clearPlayback],
  )

  /** Agenda um chunk PCM 24 kHz do modelo para reprodução sem lacunas. */
  const enqueuePlayback = useCallback((internals: SessionInternals, base64: string) => {
    const ctx = internals.playbackContext
    if (!ctx || internals.stopped) return
    const pcm = base64ToInt16(base64)
    if (pcm.length === 0) return
    const buffer = ctx.createBuffer(1, pcm.length, OUTPUT_SAMPLE_RATE)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < pcm.length; i++) data[i] = (pcm[i] ?? 0) / 32768
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    source.onended = () => {
      internals.scheduledSources = internals.scheduledSources.filter((s) => s !== source)
      // Terminou de falar e nada mais agendado → volta a ouvir.
      if (
        internals.scheduledSources.length === 0 &&
        !internals.stopped &&
        statusRef.current === 'speaking'
      ) {
        setStatus('listening')
      }
    }
    internals.scheduledSources.push(source)
    internals.nextStartTime = Math.max(ctx.currentTime, internals.nextStartTime)
    source.start(internals.nextStartTime)
    internals.nextStartTime += buffer.duration
    internals.receivedAudio = true
    // Telemetria de custo por voz: soma a duração real do áudio de saída (não é
    // estimativa por tempo de status — é o tamanho exato do PCM enfileirado).
    internals.outputAudioSeconds += buffer.duration
  }, [])

  /** Garante uma conversa para persistir os turnos (cria na primeira vez). */
  const ensureConversation = useCallback(
    async (internals: SessionInternals): Promise<string | null> => {
      const currentUid = uidRef.current
      if (!currentUid) return null
      if (internals.conversationId) return internals.conversationId
      const id = await createVoiceConversation(currentUid)
      internals.conversationId = id
      onConversationCreatedRef.current?.(id)
      return id
    },
    [],
  )

  /** Persiste um turno completo (fila serializada; falhas só logam no console). */
  const queuePersistTurn = useCallback(
    (internals: SessionInternals, turn: TurnBuffer) => {
      if (!turnHasContent(turn)) return
      internals.persistChain = internals.persistChain
        .then(async () => {
          const conversationId = await ensureConversation(internals)
          const currentUid = uidRef.current
          if (!conversationId || !currentUid) return
          await persistVoiceTurn(currentUid, conversationId, {
            userText: turn.user,
            assistantText: turn.assistant,
            toolParts: turn.toolParts,
          })
          internals.persistedAnyTurn = true
        })
        .catch((err) => {
          console.error('Falha ao persistir turno de voz:', err)
        })
    },
    [ensureConversation],
  )

  const handleServerContent = useCallback(
    (internals: SessionInternals, content: LiveServerContent) => {
      if (content.interrupted) {
        // Barge-in: o usuário falou por cima — descarta o áudio pendente.
        clearPlayback(internals)
        if (statusRef.current === 'speaking') setStatus('listening')
      }
      if (content.inputTranscription?.text) {
        internals.turn.user += content.inputTranscription.text
        const userText = internals.turn.user
        setTranscript((prev) => ({ ...prev, user: userText }))
      }
      if (content.outputTranscription?.text) {
        internals.turn.assistant += content.outputTranscription.text
        const assistantText = internals.turn.assistant
        setTranscript((prev) => ({ ...prev, assistant: assistantText }))
      }
      const parts = content.modelTurn?.parts ?? []
      for (const part of parts) {
        if (part.inlineData?.mimeType.startsWith('audio/') && part.inlineData.data) {
          enqueuePlayback(internals, part.inlineData.data)
          if (!internals.stopped && statusRef.current !== 'speaking') setStatus('speaking')
        }
      }
      if (content.turnComplete) {
        // Turno finalizado: persiste e zera o buffer de acumulação. A transcrição
        // exibida (estado `transcript`) permanece até novos fragmentos chegarem.
        const finished = internals.turn
        internals.turn = emptyTurn()
        queuePersistTurn(internals, finished)
      }
    },
    [clearPlayback, enqueuePlayback, queuePersistTurn],
  )

  const handleToolCall = useCallback(
    async (internals: SessionInternals, message: LiveServerToolCall) => {
      if (internals.stopped || !internals.session) return
      setStatus('executing-tool')
      // Conversa de origem: necessária para tools que postam o resultado assíncrono
      // de volta no chat (startEmailResearch) e para a proveniência de createReminder
      // (sourceContext.conversationId). Materializa a conversa se ainda não existir —
      // mesma conversa que persiste os turnos desta sessão. A criação faz uma escrita
      // no Firestore que pode falhar de forma transitória; nesse caso degrada para
      // null e só loga — as tools de leitura seguem executando (só startEmailResearch
      // falha, com o erro pt-BR próprio) em vez de derrubar a sessão inteira.
      let conversationId: string | null = null
      try {
        conversationId = await ensureConversation(internals)
      } catch (err) {
        console.error('Falha ao garantir conversa para a chamada de ferramenta de voz:', err)
      }
      const responses: FunctionResponse[] = []
      // Executa TODAS as chamadas do turno e responde todas juntas — possível
      // porque consumimos receive() diretamente (o helper do SDK só suportaria uma).
      for (const call of message.functionCalls) {
        setToolLabel(agentToolLabel(call.name))
        const args = (call.args ?? {}) as Record<string, unknown>
        const name = call.name as AgentToolName
        let result: ToolResult<unknown>
        try {
          // 1) Primeira chamada SEM confirmação. Tools de escrita respondem
          //    `needsConfirmation` (nada é executado); tools de leitura já retornam.
          const { data } = await executeAgentTool({
            name,
            args,
            ...(conversationId ? { conversationId } : {}),
            // Chave de idempotência estável: o id da function call do Live é o
            // mesmo em retries/reconexões, então a confirmação executa a ação
            // no máximo uma vez. Ausente ⇒ o campo é omitido.
            ...(call.id ? { idempotencyKey: call.id } : {}),
          })
          const first = data.result
          if ('needsConfirmation' in first && first.needsConfirmation === true) {
            // 2) Trava de escritas: pausa e aguarda a decisão explícita do usuário.
            //    O loop de receive() fica suspenso aqui até
            //    confirmPendingAction/cancelPendingAction (ou stop(), via
            //    releaseResources) resolver a Promise.
            setToolLabel(null)
            setStatus('awaiting-confirmation')
            setPendingConfirmation({ summary: first.confirmation.summary })
            const decision = await new Promise<'confirm' | 'cancel'>((resolve) => {
              internals.pendingConfirmationResolve = resolve
            })
            setPendingConfirmation(null)
            if (internals.stopped) return
            if (decision === 'confirm') {
              // 3) Reenvia com confirmed:true — agora a escrita executa de fato.
              setStatus('executing-tool')
              setToolLabel(agentToolLabel(call.name))
              const { data: confirmedData } = await executeAgentTool({
                name,
                args,
                confirmed: true,
                ...(conversationId ? { conversationId } : {}),
                // Mesma chave da chamada não confirmada (mesmo call.id) — o
                // backend deduplica esta execução por confirmação.
                ...(call.id ? { idempotencyKey: call.id } : {}),
              })
              const reResult = confirmedData.result
              result =
                'needsConfirmation' in reResult && reResult.needsConfirmation === true
                  ? { ok: false, error: 'A ação exige confirmação.' }
                  : reResult
            } else {
              result = { ok: false, error: 'Ação cancelada pelo usuário.' }
            }
          } else {
            result = first
          }
        } catch (err) {
          console.error(`Falha ao executar a ferramenta "${call.name}":`, err)
          result = { ok: false, error: 'Falha ao executar a ferramenta.' }
        }
        const response: FunctionResponse = { name: call.name, response: result }
        if (call.id) response.id = call.id
        responses.push(response)
        internals.turn.toolParts.push({ type: 'toolCall', toolName: call.name, toolArgs: args })
        internals.turn.toolParts.push({
          type: 'toolResult',
          toolName: call.name,
          toolResult: result,
        })
      }
      setToolLabel(null)
      if (!internals.stopped && internals.session && !internals.session.isClosed) {
        await internals.session.sendFunctionResponses(responses)
        setStatus('listening')
      }
    },
    [ensureConversation],
  )

  /** Confirma a ação de escrita pendente (a trava): libera o modelo para executar. */
  const confirmPendingAction = useCallback(() => {
    const internals = internalsRef.current
    const resolve = internals?.pendingConfirmationResolve
    if (!internals || !resolve) return
    internals.pendingConfirmationResolve = null
    setPendingConfirmation(null)
    setStatus('executing-tool')
    resolve('confirm')
  }, [])

  /** Cancela a ação de escrita pendente: devolve um cancelamento ao modelo. */
  const cancelPendingAction = useCallback(() => {
    const internals = internalsRef.current
    const resolve = internals?.pendingConfirmationResolve
    if (!internals || !resolve) return
    internals.pendingConfirmationResolve = null
    setPendingConfirmation(null)
    setStatus('listening')
    resolve('cancel')
  }, [])

  const stop = useCallback(async () => {
    const internals = internalsRef.current
    if (!internals || internals.stopped) return
    internalsRef.current = null
    setStatus('ending')
    setToolLabel(null)
    setPendingConfirmation(null)
    internals.stopped = true

    // Telemetria de custo por voz — best-effort, nunca bloqueia nem quebra o
    // encerramento (erro só loga). `inputAudioSeconds` é a duração total da sessão
    // ativa (do handshake concluído, `startedAt`, até agora) — o silêncio também é
    // cobrado pelo provedor, então não tentamos descontá-lo. `outputAudioSeconds` é
    // a soma exata do PCM que o modelo efetivamente reproduziu (acumulada em
    // `enqueuePlayback`, não uma estimativa por tempo de status). Só reporta se o
    // handshake chegou a concluir (`startedAt > 0`) e houve sessão real.
    if (internals.startedAt > 0) {
      const inputAudioSeconds = (Date.now() - internals.startedAt) / 1000
      const outputAudioSeconds = internals.outputAudioSeconds
      if (inputAudioSeconds > 0 || outputAudioSeconds > 0) {
        recordVoiceUsage({ inputAudioSeconds, outputAudioSeconds }).catch((err) => {
          console.error('Falha ao registrar uso de voz (telemetria, não afeta a sessão):', err)
        })
      }
    }

    try {
      // Flush do último turno parcial (transcrições que não fecharam com turnComplete).
      const pending = internals.turn
      internals.turn = emptyTurn()
      queuePersistTurn(internals, pending)
      // Degradação graciosa: o marcador só entra quando NENHUM turno transcrito
      // foi persistido (`persistedAnyTurn` é setado dentro da persistChain, então
      // aqui já reflete todos os turnos anteriores nesta fila serializada) e a
      // sessão realmente aconteceu (houve áudio do modelo). Com transcrição
      // ligada os turnos persistem e este marcador não é gravado — sem
      // duplicidade; sem transcrição, é o único registro da conversa.
      internals.persistChain = internals.persistChain
        .then(async () => {
          if (internals.persistedAnyTurn || !internals.receivedAudio) return
          const conversationId = await ensureConversation(internals)
          const currentUid = uidRef.current
          if (!conversationId || !currentUid) return
          await persistVoiceMarker(currentUid, conversationId)
        })
        .catch((err) => {
          console.error('Falha ao persistir marcador da sessão de voz:', err)
        })
      await internals.persistChain
    } finally {
      releaseResources(internals)
      // Só rotula 'ended' se nenhuma nova sessão começou durante o await do
      // flush. `stop()` zera `internalsRef` no início; um `start()` posterior o
      // repovoa e já setou 'listening'/etc. — não sobrescreva o rótulo da nova
      // sessão com este 'ended' atrasado.
      if (internalsRef.current === null) setStatus('ended')
    }
  }, [ensureConversation, queuePersistTurn, releaseResources])

  const stopRef = useRef(stop)
  stopRef.current = stop

  // Referência mutável para a reconexão: quebra o ciclo runReceiveLoop → reconnect
  // → runReceiveLoop sem recriar callbacks a cada render.
  const reconnectRef = useRef<((internals: SessionInternals, session: LiveSession) => void) | null>(
    null,
  )

  /**
   * Loop único de consumo do receive() (ver descoberta no cabeçalho). Garante-se
   * que NUNCA haja dois loops simultâneos no mesmo `session`: cada término
   * (goingAway/fim natural/erro) sai do `for await` e SÓ então decide reconectar
   * — a reconexão bem-sucedida agenda um novo loop depois deste ter retornado.
   */
  const runReceiveLoop = useCallback(
    async (internals: SessionInternals, session: LiveSession) => {
      let reason: LoopEndReason = 'natural'
      try {
        for await (const message of session.receive()) {
          if (internals.stopped) break
          if (message.type === 'serverContent') {
            handleServerContent(internals, message as LiveServerContent)
          } else if (message.type === 'toolCall') {
            await handleToolCall(internals, message as LiveServerToolCall)
          } else if (message.type === 'toolCallCancellation') {
            // As chamadas são executadas de forma síncrona neste loop, então um
            // cancelamento chega depois de já termos respondido — apenas registra.
            console.warn('Live API cancelou chamadas de ferramenta:', message)
          } else if (message.type === 'sessionResumptionUpdate') {
            // Guarda o handle enquanto retomável — é o que permite reabrir a
            // MESMA conversa quando o WS cair.
            internals.resumptionHandle = nextResumptionHandle(
              internals.resumptionHandle,
              message as LiveSessionResumptionUpdate,
            )
          } else if (message.type === 'goingAwayNotice') {
            // O servidor vai derrubar a conexão em breve — reconecta proativamente
            // (ou encerra com cortesia se não houver resumption).
            reason = 'goingAway'
            break
          }
        }
      } catch (err) {
        const decision = decideLoopEnd({
          reason: 'error',
          stopped: internals.stopped,
          hasHandle: internals.resumptionHandle !== null,
          attempts: internals.reconnectAttempts,
          maxAttempts: VOICE_RECONNECT_MAX_ATTEMPTS,
        })
        if (decision === 'reconnect') {
          reconnectRef.current?.(internals, session)
          return
        }
        if (decision === 'error') {
          console.error('Erro na sessão de voz:', err)
          releaseResources(internals)
          internalsRef.current = null
          setPendingConfirmation(null)
          setError('A conexão da conversa por voz foi perdida. Tente novamente.')
          setStatus('error')
        }
        // decision === 'stop': o stop()/unmount já está encerrando — nada a fazer.
        return
      }
      // Fim sem exceção: servidor fechou o stream ou avisou goingAway.
      const decision = decideLoopEnd({
        reason,
        stopped: internals.stopped,
        hasHandle: internals.resumptionHandle !== null,
        attempts: internals.reconnectAttempts,
        maxAttempts: VOICE_RECONNECT_MAX_ATTEMPTS,
      })
      if (decision === 'reconnect') {
        reconnectRef.current?.(internals, session)
        return
      }
      // 'stop' (fim natural/goingAway sem resumption) → encerra com cortesia.
      if (!internals.stopped) void stopRef.current()
    },
    [handleServerContent, handleToolCall, releaseResources],
  )

  /**
   * Reabre a MESMA sessão após uma queda, com backoff exponencial. Retoma o WS
   * com o handle guardado (`resumeSession` fecha o WS atual e reabre com a mesma
   * config), reseta o contador e reentra no loop. Os buffers de turno/persistência
   * e o `conversationId` NÃO são tocados — os turnos atravessam o gap. Checa
   * `stopped` após CADA await para não competir com um `stop()` concorrente.
   */
  const reconnect = useCallback(
    async (internals: SessionInternals, session: LiveSession) => {
      if (internals.stopped || internals.reconnecting) return
      const handle = internals.resumptionHandle
      if (!handle) {
        // Sem handle não há como retomar — encerra com cortesia.
        if (!internals.stopped) void stopRef.current()
        return
      }
      internals.reconnecting = true
      setStatus('reconnecting')
      // Descarta o áudio em voo: a nova conexão recomeça o fluxo do servidor.
      clearPlayback(internals)
      try {
        while (!internals.stopped && internals.reconnectAttempts < VOICE_RECONNECT_MAX_ATTEMPTS) {
          const delay = reconnectDelayMs(internals.reconnectAttempts, VOICE_RECONNECT_BASE_DELAY_MS)
          await new Promise((resolve) => setTimeout(resolve, delay))
          if (internals.stopped) return
          internals.reconnectAttempts += 1
          try {
            await session.resumeSession({ handle })
            if (internals.stopped) {
              void session.close().catch(() => undefined)
              return
            }
            // Sucesso: zera o contador e reentra num ÚNICO loop novo.
            internals.reconnectAttempts = 0
            internals.reconnecting = false
            setStatus('listening')
            void runReceiveLoop(internals, session)
            return
          } catch (err) {
            console.warn(
              `Reconexão da voz falhou (tentativa ${internals.reconnectAttempts}/${VOICE_RECONNECT_MAX_ATTEMPTS}):`,
              err,
            )
            if (internals.stopped) return
          }
        }
        // Esgotou as tentativas (e não foi encerrado): fluxo de erro pt-BR.
        if (!internals.stopped) {
          releaseResources(internals)
          internalsRef.current = null
          setPendingConfirmation(null)
          setError('Não foi possível retomar a conversa por voz. Tente novamente.')
          setStatus('error')
        }
      } finally {
        internals.reconnecting = false
      }
    },
    [clearPlayback, releaseResources, runReceiveLoop],
  )

  reconnectRef.current = (internals, session) => {
    void reconnect(internals, session)
  }

  const start = useCallback(
    async (conversationId?: string) => {
      if (internalsRef.current) return // sessão já ativa
      const internals: SessionInternals = {
        stopped: false,
        session: null,
        captureContext: null,
        playbackContext: null,
        mediaStream: null,
        sourceNode: null,
        workletNode: null,
        workletUrl: null,
        scheduledSources: [],
        nextStartTime: 0,
        timer: null,
        startedAt: 0,
        outputAudioSeconds: 0,
        conversationId: conversationId ?? null,
        turn: emptyTurn(),
        persistChain: Promise.resolve(),
        persistedAnyTurn: false,
        receivedAudio: false,
        toolsEnabled: false,
        transcriptionEnabled: false,
        modelDowngraded: false,
        resumptionHandle: null,
        resumptionEnabled: false,
        reconnecting: false,
        reconnectAttempts: 0,
        pendingConfirmationResolve: null,
      }
      internalsRef.current = internals
      setError(null)
      setToolLabel(null)
      setElapsed(0)
      setTranscript({ user: '', assistant: '' })
      setToolsEnabled(true)
      setTranscriptionEnabled(true)
      setModelDowngraded(false)
      setPendingConfirmation(null)

      try {
        setStatus('requesting-mic')
        // Os AudioContexts são criados o mais cedo possível dentro do gesto do
        // usuário (política de autoplay dos navegadores).
        internals.captureContext = new AudioContext()
        internals.playbackContext = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE })
        if (internals.captureContext.state === 'suspended') {
          await internals.captureContext.resume()
        }
        if (internals.playbackContext.state === 'suspended') {
          await internals.playbackContext.resume()
        }
        internals.nextStartTime = internals.playbackContext.currentTime

        internals.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (internals.stopped) {
          // stop() chegou enquanto o microfone era solicitado — libera tudo
          // (inclusive o stream recém-atribuído) e sai.
          releaseResources(internals)
          return
        }

        setStatus('connecting')
        const { data: config } = await getVoiceSessionConfig(
          conversationId ? { conversationId } : {},
        )
        // Ferramentas desabilitadas na conversa de origem: removidas das
        // declarações da sessão Live (o modelo nem as enxerga). Numa sessão nova
        // (sem conversationId) o backend devolve vazio ⇒ todas habilitadas.
        const disabledTools = config.disabledTools ?? []
        // Sem uma voz escolhida, as variantes voice:true/false produzem config
        // idêntica (a voz default do modelo) e colapsam — dedupe pela config
        // efetiva evita um retry redundante do handshake.
        const hasVoice = Boolean(config.voiceName)
        const seenVariants = new Set<string>()
        const variants = LIVE_CONNECT_VARIANTS.filter((variant) => {
          const key = `${variant.tools}:${hasVoice && variant.voice}:${variant.transcription}:${variant.compress}`
          if (seenVariants.has(key)) return false
          seenVariants.add(key)
          return true
        })
        // Handshake resiliente em três dimensões, nesta ordem de prioridade:
        // (1) SESSION RESUMPTION: tenta primeiro com `connect({})` (habilita os
        //     updates de resumption → sessão longa com reconexão); se TODAS as
        //     combinações falharem com `{}`, degrada para `connect()` sem
        //     resumption (a sessão volta ao teto local de 9 min).
        // (2) MODELO: o preferido do usuário e, se ele falhar, o default (live).
        // (3) VARIANTE: compressão/voz/ferramentas/transcrição dentro do modelo.
        // Fica com a primeira combinação que conectar, priorizando resumption,
        // depois compressão, depois manter as ferramentas.
        const models = [...new Set([config.model, GEMINI_MODELS.live])]
        const resumptionModes = [true, false] as const
        let session: LiveSession | null = null
        let connectedWithTools = false
        let connectedWithTranscription = false
        let connectedWithCompression = false
        let connectedModel = config.model
        let connectedResumption = false
        let lastConnectError: unknown = null
        for (const useResumption of resumptionModes) {
          if (session || internals.stopped) break
          for (const model of models) {
            if (session || internals.stopped) break
            for (const variant of variants) {
              if (internals.stopped) break
              try {
                const liveModel = createLiveModel({
                  model,
                  systemPrompt: config.systemPrompt,
                  voiceName: variant.voice ? config.voiceName : undefined,
                  withTranscription: variant.transcription,
                  withTools: variant.tools,
                  withCompression: variant.compress,
                  disabledTools,
                })
                // `connect({})` liga os updates de resumption; `connect()` (sem
                // arg) NÃO — e nesse caso `resumeSession()` lançaria UNSUPPORTED.
                const connectPromise = useResumption ? liveModel.connect({}) : liveModel.connect()
                const timeoutPromise = new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('Timeout connecting to live model')), 8000),
                )
                try {
                  session = await Promise.race([connectPromise, timeoutPromise])
                } catch (raceErr) {
                  // Previne vazamento de conexão caso a promise atrase mas resolva depois do timeout
                  connectPromise
                    .then((s) => s.close().catch(() => undefined))
                    .catch(() => undefined)
                  throw raceErr
                }
                connectedWithTools = variant.tools
                connectedWithTranscription = variant.transcription
                connectedWithCompression = variant.compress
                connectedModel = model
                connectedResumption = useResumption
                break
              } catch (connectErr) {
                lastConnectError = connectErr
                console.warn(
                  `Handshake da voz falhou (resumption: ${useResumption ? 'on' : 'off'}, modelo: ${model}, ferramentas: ${variant.tools ? 'on' : 'off'}, compressão: ${variant.compress ? 'on' : 'off'}, voz: ${variant.voice ? 'escolhida' : 'default'}, transcrição: ${variant.transcription ? 'on' : 'off'}):`,
                  connectErr,
                )
              }
            }
          }
        }

        if (session) internals.session = session
        if (internals.stopped) {
          // stop() chegou durante a conexão — libera tudo (inclusive a sessão que
          // eventualmente abriu) e sai, como no fluxo original.
          releaseResources(internals)
          return
        }
        if (!session) {
          // Todas as combinações falharam. Relança o último erro para o catch
          // externo tratar (mostra a mensagem pt-BR ao usuário e libera recursos).
          throw lastConnectError ?? new Error('Não foi possível conectar a sessão de voz.')
        }
        internals.toolsEnabled = connectedWithTools
        setToolsEnabled(connectedWithTools)
        internals.transcriptionEnabled = connectedWithTranscription
        setTranscriptionEnabled(connectedWithTranscription)
        internals.resumptionEnabled = connectedResumption
        const modelWasDowngraded = connectedModel !== config.model
        internals.modelDowngraded = modelWasDowngraded
        setModelDowngraded(modelWasDowngraded)
        console.info(
          `Sessão de voz conectada (resumption: ${connectedResumption ? 'on' : 'off'}, modelo: ${connectedModel}${modelWasDowngraded ? ' [fallback]' : ''}, ferramentas: ${connectedWithTools ? 'on' : 'off'}, compressão: ${connectedWithCompression ? 'on' : 'off'}, transcrição: ${connectedWithTranscription ? 'on' : 'off'})`,
        )

        // Grafo de captura: microfone → source → worklet (16 kHz Int16 chunks).
        const workletBlob = new Blob([CAPTURE_WORKLET_SOURCE], { type: 'application/javascript' })
        internals.workletUrl = URL.createObjectURL(workletBlob)
        await internals.captureContext.audioWorklet.addModule(internals.workletUrl)
        if (internals.stopped) {
          releaseResources(internals)
          return
        }
        internals.sourceNode = internals.captureContext.createMediaStreamSource(
          internals.mediaStream,
        )
        internals.workletNode = new AudioWorkletNode(
          internals.captureContext,
          CAPTURE_PROCESSOR_NAME,
          {
            processorOptions: {
              targetSampleRate: INPUT_SAMPLE_RATE,
              chunkSamples: CAPTURE_CHUNK_SAMPLES,
            },
          },
        )
        internals.sourceNode.connect(internals.workletNode)
        internals.workletNode.port.onmessage = (event: MessageEvent<Int16Array>) => {
          if (internals.stopped || !internals.session || internals.session.isClosed) return
          void internals.session
            .sendAudioRealtime({ data: int16ToBase64(event.data), mimeType: 'audio/pcm' })
            .catch(() => undefined)
        }

        // Cronômetro contínuo + teto de segurança. Com resumption ativa o relógio
        // atravessa as reconexões e só encerra no teto de custo (1h); sem
        // resumption (fallback) cai no teto local antigo (9 min).
        const capSeconds = internals.resumptionEnabled
          ? VOICE_SESSION_HARD_CAP_SECONDS
          : FALLBACK_CAP_SECONDS
        internals.startedAt = Date.now()
        internals.timer = setInterval(() => {
          const seconds = Math.floor((Date.now() - internals.startedAt) / 1000)
          setElapsed(seconds)
          if (seconds >= capSeconds) void stopRef.current()
        }, 1000)

        setStatus('listening')
        void runReceiveLoop(internals, session)
      } catch (err) {
        const rawMessage = err instanceof Error ? err.message : String(err)
        console.error('Falha ao iniciar a sessão de voz:', rawMessage, err)
        // `stopped` já true indica que stop() interveio no meio do start —
        // nesse caso não sobrescrevemos o estado 'ended' com 'error'.
        const cancelled = internals.stopped
        releaseResources(internals)
        if (internalsRef.current === internals) internalsRef.current = null
        if (!cancelled) {
          setError(mapStartError(err))
          setStatus('error')
        }
      }
    },
    [releaseResources, runReceiveLoop],
  )

  // Cleanup completo no unmount — nunca deixar o microfone aberto.
  useEffect(() => {
    return () => {
      const internals = internalsRef.current
      if (internals) {
        internalsRef.current = null
        // Sem persistência assíncrona no unmount; apenas libera os recursos.
        releaseResources(internals)
      }
    }
  }, [releaseResources])

  return {
    status,
    error,
    toolLabel,
    elapsed,
    transcript,
    toolsEnabled,
    transcriptionEnabled,
    modelDowngraded,
    pendingConfirmation,
    confirmPendingAction,
    cancelPendingAction,
    start,
    stop,
  }
}
